import { useState, useCallback, useRef } from "react";

const HIGHLIGHT_DURATION_MS = 3000;

/**
 * Hook that manages PDF search text state.
 *
 * Sets a search term that flows into the PdfViewer's `customTextRenderer`
 * for highlighting, and auto-clears it after a timeout.
 */
export const usePdfTextSearch = () => {
  const [pdfSearchText, setPdfSearchText] = useState<string | null>(null);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSearch = useCallback(() => {
    setPdfSearchText(null);
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  }, []);

  const searchInPdf = useCallback((text: string) => {
    clearSearch();

    if (!text.trim()) return;

    setPdfSearchText(text.trim());

    clearTimerRef.current = setTimeout(() => {
      setPdfSearchText(null);
      clearTimerRef.current = null;
    }, HIGHLIGHT_DURATION_MS);
  }, [clearSearch]);

  return { pdfSearchText, searchInPdf, clearSearch };
};
